import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, Loader2, Target, ListChecks, BookOpen, Medal, CalendarCheck } from "lucide-react";
import { academy } from "@/lib/academy";
import { apiErr } from "@/lib/api";
import { Md } from "@/components/academy/Md";
import { Quiz } from "@/components/academy/Quiz";
import { CaseFiles } from "@/components/academy/CaseFiles";
import { EmergencyNotice, LessonFooter } from "@/components/academy/AcademyParts";
import { AcademyFrame } from "@/components/academy/AcademyFrame";
import { OpsSimulation } from "@/components/academy/OpsSimulation";

function useHeartbeat(moduleId, lessonId) {
  const ref = useRef(Date.now());
  useEffect(() => {
    ref.current = Date.now();
    const send = () => {
      const s = Math.round((Date.now() - ref.current) / 1000);
      ref.current = Date.now();
      if (s > 0 && document.visibilityState === "visible") academy.post(`/modules/${moduleId}/heartbeat`, { lesson_id: lessonId, seconds: Math.min(s, 300) }).catch(() => {});
    };
    const id = setInterval(send, 30000);
    return () => { clearInterval(id); send(); };
  }, [moduleId, lessonId]);
}

function StepNav({ mod, step, setStep }) {
  const items = [["overview", "Overview", mod.summary.pct === 100, BookOpen], ...mod.lessons.map((l) => [l.id, l.title, l.done, null])];
  if (mod.mission) items.push(["mission", `Mini-mission: ${mod.mission.title}`, mod.mission_done, Target]);
  items.push(["quiz", "Quiz", mod.quiz.passed, ListChecks]);
  return (
    <nav className="space-y-1" data-testid="academy-steps">
      {items.map(([id, label, done, Icon]) => (
        <button key={id} data-testid={`academy-step-${id}`} onClick={() => setStep(id)}
          className={`flex w-full items-start gap-2.5 rounded-lg px-3 py-2 text-left text-[13px] transition-colors ${step === id ? "bg-blue-600/15 text-zinc-50" : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"}`}>
          {done ? <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-emerald-400" /> : Icon ? <Icon size={15} className="mt-0.5 shrink-0" /> : <Circle size={15} className="mt-0.5 shrink-0" />}
          <span>{label}</span>
        </button>
      ))}
    </nav>
  );
}

function Overview({ mod, onStart }) {
  return (
    <div data-testid="academy-module-overview">
      <div className="text-[12px] uppercase tracking-[0.2em] text-blue-400">Module {mod.order} · {mod.minutes} min</div>
      <h1 className="mt-1 text-3xl font-bold tracking-tight text-zinc-50 sm:text-4xl">{mod.title}</h1>
      <p className="mt-2 text-[15px] text-zinc-400">{mod.subtitle}</p>
      {mod.objectives.length > 0 && (
        <div className="mt-6 rounded-xl border border-zinc-800 bg-zinc-950/60 p-5">
          <div className="text-[12px] uppercase tracking-wider text-zinc-500">You will learn to…</div>
          <ul className="mt-2 list-disc space-y-1.5 pl-5 text-[14px] text-zinc-300 marker:text-blue-500">{mod.objectives.map((o) => <li key={o}>{o}</li>)}</ul>
        </div>
      )}
      <div className="mt-5 flex items-center gap-2 text-[14px] text-zinc-300"><Medal size={16} className="text-[#FFCC00]" /> Pass the quiz to earn the <b className="text-zinc-50">{mod.badge}</b> badge and reach <b className="text-zinc-50">{mod.level_title}</b>.</div>
      <button data-testid="academy-module-start" onClick={onStart} className="mt-6 inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-blue-500">Start learning <ArrowRight size={16} /></button>
    </div>
  );
}

function Lesson({ lesson, onDone, busy }) {
  return (
    <article data-testid={`academy-lesson-${lesson.id}`}>
      <h1 className="text-2xl font-bold tracking-tight text-zinc-50 sm:text-3xl">{lesson.title}</h1>
      {lesson.playbook && <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-zinc-800 px-3 py-1 text-[12px] text-zinc-400" data-testid="academy-last-reviewed"><CalendarCheck size={13} /> Last reviewed: October 2026</div>}
      <Md className="mt-4">{lesson.body}</Md>
      <button data-testid="academy-lesson-done" onClick={onDone} disabled={busy}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-blue-500 disabled:opacity-50">
        {busy && <Loader2 size={15} className="animate-spin" />} {lesson.done ? "Next" : "Done, next"} <ArrowRight size={16} />
      </button>
    </article>
  );
}

function Mission({ mod, onToggle, busy }) {
  return (
    <div data-testid="academy-mission">
      <div className="text-[12px] uppercase tracking-[0.2em] text-[#FFCC00]">Mini-mission · {mod.mission.minutes} min</div>
      <h1 className="mt-1 text-2xl font-bold text-zinc-50 sm:text-3xl">{mod.mission.title}</h1>
      <Md className="mt-4">{mod.mission.body}</Md>
      <p className="mt-3 text-[13px] text-zinc-500">Missions are self-reported. Please do not upload screenshots of reports, accounts or harmful content.</p>
      <button data-testid="academy-mission-toggle" onClick={onToggle} disabled={busy}
        className={`mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[14px] font-semibold disabled:opacity-50 ${mod.mission_done ? "border border-emerald-500/50 text-emerald-300" : "bg-[#FFCC00] text-zinc-950"}`}>
        <CheckCircle2 size={16} /> {mod.mission_done ? "Done (click to undo)" : "I did it"}
      </button>
    </div>
  );
}

export default function AcademyModulePage() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [mod, setMod] = useState(null);
  const [step, setStep] = useState("overview");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => academy.get(`/modules/${moduleId}`).then((r) => setMod(r.data)).catch((e) => {
    toast.error(apiErr(e?.response?.data?.detail));
    navigate("/academy");
  }), [moduleId, navigate]);
  useEffect(() => { load(); setStep("overview"); }, [load]);
  useHeartbeat(moduleId, step.startsWith(moduleId) ? step : null);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  if (!mod) return <AcademyFrame><div className="grid min-h-[50vh] place-items-center"><Loader2 className="animate-spin text-zinc-500" /></div></AcademyFrame>;

  const order = ["overview", ...mod.lessons.map((l) => l.id), ...(mod.mission ? ["mission"] : []), "quiz"];
  const goNext = () => setStep(order[Math.min(order.indexOf(step) + 1, order.length - 1)]);
  const lesson = mod.lessons.find((l) => l.id === step);
  const doneLesson = async () => {
    setBusy(true);
    try {
      if (!lesson.done) await academy.post(`/modules/${mod.id}/lessons/${lesson.id}/complete`);
      await load();
      goNext();
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const toggleMission = async () => {
    setBusy(true);
    try { await academy.post(`/modules/${mod.id}/mission`, { done: !mod.mission_done }); await load(); } finally { setBusy(false); }
  };
  const simple = mod.kind !== "module";

  return (
    <AcademyFrame>
      <button data-testid="academy-back" onClick={() => navigate("/academy")} className="mb-5 inline-flex items-center gap-1.5 text-[13px] text-zinc-400 hover:text-zinc-100"><ArrowLeft size={15} /> Dashboard</button>
      {mod.emergency && <div className="mb-5"><EmergencyNotice /></div>}
      {mod.kind === "simulation" ? (
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-50 sm:text-4xl">{mod.full_title}</h1>
          <p className="mt-2 mb-6 text-[14px] text-zinc-400">{mod.subtitle}</p>
          <OpsSimulation mod={mod} reload={load} onBack={() => navigate("/academy")} />
          <LessonFooter />
        </div>
      ) : simple ? (
        <div className="mx-auto max-w-3xl">
          <h1 className="text-3xl font-bold tracking-tight text-zinc-50 sm:text-4xl">{mod.full_title}</h1>
          <p className="mt-2 mb-6 text-[14px] text-zinc-400">{mod.subtitle}</p>
          {mod.kind === "capstone" ? <CaseFiles mod={mod} reload={load} /> : (
            <div className="rounded-2xl border border-zinc-800/70 bg-[#111113] p-6 md:p-8">
              <Md className="mb-6">{mod.intro}</Md>
              <Quiz mod={mod} onDone={load} onBack={() => navigate("/academy")} />
            </div>
          )}
          <LessonFooter />
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
          <aside className="order-2 lg:order-1 lg:sticky lg:top-28 lg:self-start">
            <div className="mb-3 px-3 text-[12px] uppercase tracking-wider text-zinc-500">Module {mod.order} · {mod.summary.pct}%</div>
            <StepNav mod={mod} step={step} setStep={setStep} />
          </aside>
          <section className="order-1 min-w-0 rounded-2xl border border-zinc-800/70 bg-[#111113] p-6 md:p-8 lg:order-2">
            {step === "overview" && <Overview mod={mod} onStart={goNext} />}
            {lesson && <Lesson lesson={lesson} onDone={doneLesson} busy={busy} />}
            {step === "mission" && <Mission mod={mod} onToggle={toggleMission} busy={busy} />}
            {step === "quiz" && <Quiz mod={mod} onDone={load} onBack={() => navigate("/academy")} />}
            <LessonFooter />
          </section>
        </div>
      )}
    </AcademyFrame>
  );
}
